import React, { useState, useEffect } from 'react';
import { motion as Motion, AnimatePresence } from 'framer-motion';
import { X, Check, ArrowRight, User, Mail, Building, MessageSquare, AlertCircle } from 'lucide-react';

/* Lead capture for the scope configurator. Same shell and same endpoint as
   the scope-call modal: one POST to /api/booking, which sends the agency
   notification and the visitor's confirmation. Everything the configurator
   produced travels with it, so the follow-up call starts from the numbers
   the visitor already saw. */

const COPY = {
  de: {
    eyebrow: 'Scope senden',
    title: 'Dein Scope, geprüft',
    intro: 'CRE8NOW schaut sich deine Konfiguration an und meldet sich innerhalb eines Werktags mit einer konkreten Einschätzung zurück.',
    price: 'Geschätzter Festpreis',
    delivery: 'Geschätzte Lieferzeit',
    days: 'Werktage',
    additions: 'Erweiterungen',
    paymentLabel: 'Zahlung',
    paymentFirst: '70 % vor Projektstart',
    paymentSecond: '30 % vor der Datenübergabe',
    name: 'Name',
    email: 'Geschäftliche E-Mail',
    company: 'Unternehmen',
    notes: 'Projektbeschreibung (optional)',
    send: 'Scope senden',
    sending: 'Wird gesendet…',
    required: 'Bitte Name, geschäftliche E-Mail und Unternehmen ausfüllen.',
    successTitle: 'Scope angekommen',
    successText: 'Eine Bestätigung mit deiner kompletten Konfiguration ist unterwegs zu dir. CRE8NOW meldet sich innerhalb eines Werktags mit einer konkreten Einschätzung.',
    errorTitle: 'Da ging etwas schief',
    errorText: 'Der Versand hat nicht geklappt. Schreib direkt an hello@cre8now.design, dann kommt dein Scope von Hand durch.',
    close: 'Schließen',
    disclaimer: 'Unverbindlich. CRE8NOW bestätigt den exakten Umfang, den finalen Festpreis und den Zeitplan vor dem Projektstart.',
  },
  en: {
    eyebrow: 'Send your scope',
    title: 'Your scope, reviewed',
    intro: 'CRE8NOW reviews your configuration and comes back within one business day with a concrete assessment.',
    price: 'Estimated fixed price',
    delivery: 'Estimated delivery',
    days: 'business days',
    additions: 'additions',
    paymentLabel: 'Payment',
    paymentFirst: '70% before the project starts',
    paymentSecond: '30% before the data handover',
    name: 'Name',
    email: 'Work email',
    company: 'Company',
    notes: 'Project description (optional)',
    send: 'Send scope',
    sending: 'Sending…',
    required: 'Please fill in name, work email and company.',
    successTitle: 'Scope received',
    successText: 'A confirmation with your full configuration is on its way to you. CRE8NOW gets back to you within one business day with a concrete assessment.',
    errorTitle: 'Something went wrong',
    errorText: 'The submission failed. Write to hello@cre8now.design directly and your scope gets picked up by hand.',
    close: 'Close',
    disclaimer: 'No commitment. CRE8NOW confirms the exact scope, final fixed price and timeline before the project starts.',
  },
};

const Field = ({ id, label, icon, type = 'text', value, onChange, color, textarea }) => {
  const Icon = icon;
  return (
  <div>
    <label
      htmlFor={id}
      className="font-mono-swiss text-[9px] tracking-[0.25em] uppercase text-text-muted flex items-center gap-2 mb-2"
    >
      <Icon size={12} style={{ color }} />
      {label}
    </label>
    {textarea ? (
      <textarea
        id={id}
        rows={3}
        value={value}
        onChange={onChange}
        className="w-full bg-transparent border-b border-border-color py-2.5 !text-[16px] resize-none focus:outline-none focus:border-text-color transition-colors"
      />
    ) : (
      <input
        id={id}
        type={type}
        value={value}
        onChange={onChange}
        className="w-full bg-transparent border-b border-border-color py-2.5 !text-[16px] focus:outline-none focus:border-text-color transition-colors"
      />
    )}
  </div>
  );
};

const PrototypeScopeModal = ({ isOpen, onClose, color, lang = 'de', scope }) => {
  const c = COPY[lang] || COPY.en;
  const [form, setForm] = useState({ name: '', email: '', company: '', notes: '' });
  const [status, setStatus] = useState(null); // null | sending | success | error | invalid

  // Lock the page behind the modal, and let Escape close it.
  useEffect(() => {
    if (!isOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  // A reopened modal starts clean rather than showing the previous result.
  useEffect(() => {
    if (isOpen) setStatus(null);
  }, [isOpen]);

  if (!scope) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    if (!form.name.trim() || !form.company.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      setStatus('invalid');
      return;
    }
    setStatus('sending');
    try {
      const response = await fetch('/api/booking', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          company: form.company.trim(),
          notes: form.notes.trim(),
          type: 'prototype_inquiry',
          focus: 'Rapid Prototyping',
          lang,
          origin: window.location.origin,
          ...scope.payload,
        }),
      });
      if (!response.ok) throw new Error('Submission failed');
      setStatus('success');
      if (typeof window !== 'undefined' && window.gtag) {
        window.gtag('event', 'prototype_scope_submit', {
          estimated_price: scope.totalPrice,
          estimated_days: scope.totalDays,
        });
      }
    } catch (error) {
      console.error(error);
      setStatus('error');
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
          onClick={onClose}
          role="dialog"
          aria-modal="true"
        >
          <Motion.div
            initial={{ y: 50, opacity: 0, scale: 0.95 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 50, opacity: 0, scale: 0.95 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="w-full max-w-2xl max-h-[92dvh] overflow-y-auto bg-bg-color border border-border-color rounded-2xl shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative p-6 md:p-8">
              <button
                onClick={onClose}
                aria-label={c.close}
                className="absolute top-4 right-4 p-2 hover:bg-text-color/5 rounded-full transition-colors z-10"
              >
                <X size={20} />
              </button>

              {status === 'success' ? (
                <div className="py-6 text-center">
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-6"
                    style={{ backgroundColor: color }}
                  >
                    <Check size={26} strokeWidth={3} className="text-white" />
                  </div>
                  <h4 className="font-black tracking-tighter mb-3">{c.successTitle}</h4>
                  <p className="text-sm text-text-muted font-light leading-relaxed max-w-md mx-auto mb-8">
                    {c.successText}
                  </p>
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-8 py-3.5 rounded-[4px] font-black uppercase tracking-widest text-[12px] text-white"
                    style={{ backgroundColor: color }}
                  >
                    {c.close}
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-3 mb-4 pr-10">
                    <span className="w-[5px] h-[5px] flex-shrink-0" style={{ backgroundColor: color }} />
                    <span className="font-mono-swiss text-[10px] tracking-[0.3em] uppercase text-text-muted">
                      {c.eyebrow}
                    </span>
                  </div>

                  <h4 className="font-black tracking-tighter text-text-color mb-3">
                    {c.title}
                  </h4>
                  <p className="text-sm text-text-muted font-light leading-relaxed mb-6">{c.intro}</p>

                  {/* The two numbers travel with the form, so restate them here */}
                  <div
                    className="grid grid-cols-2 rounded-[4px] overflow-hidden mb-7"
                    style={{ backgroundColor: color, color: '#ffffff' }}
                  >
                    <div className="px-5 py-4 border-r border-white/20">
                      <div className="font-mono-swiss text-[9px] tracking-[0.15em] uppercase text-white/65 mb-1">
                        {c.price}
                      </div>
                      <div className="font-black leading-none tracking-tight tabular-nums text-[clamp(26px,3vw,36px)]">
                        {scope.priceLabel}
                      </div>
                    </div>
                    <div className="px-5 py-4">
                      <div className="font-mono-swiss text-[9px] tracking-[0.15em] uppercase text-white/65 mb-1">
                        {c.delivery}
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="font-black leading-none tracking-tight tabular-nums text-[clamp(26px,3vw,36px)]">
                          {scope.totalDays}
                        </span>
                        <span className="font-mono-swiss text-[9px] uppercase tracking-[0.15em] text-white/65">
                          {c.days}
                        </span>
                      </div>
                    </div>
                    <div className="col-span-2 flex flex-wrap items-center gap-x-5 gap-y-1.5 px-5 py-3 border-t border-white/25">
                      <span className="font-mono-swiss text-[9px] tracking-[0.2em] uppercase text-white/60">
                        {c.paymentLabel}
                      </span>
                      {[c.paymentFirst, c.paymentSecond].map((term) => (
                        <span key={term} className="flex items-center gap-2 text-[11px] font-black uppercase tracking-[0.08em]">
                          <span className="w-[4px] h-[4px] flex-shrink-0 bg-white/50" />
                          {term}
                        </span>
                      ))}
                    </div>
                  </div>

                  <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <Field
                        id="scope-name"
                        label={c.name}
                        icon={User}
                        color={color}
                        value={form.name}
                        onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      />
                      <Field
                        id="scope-email"
                        label={c.email}
                        icon={Mail}
                        type="email"
                        color={color}
                        value={form.email}
                        onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      />
                      <div className="md:col-span-2">
                        <Field
                          id="scope-company"
                          label={c.company}
                          icon={Building}
                          color={color}
                          value={form.company}
                          onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                        />
                      </div>
                      <div className="md:col-span-2">
                        <Field
                          id="scope-notes"
                          label={c.notes}
                          icon={MessageSquare}
                          color={color}
                          textarea
                          value={form.notes}
                          onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="mt-6 font-mono-swiss text-[10px] tracking-[0.15em] uppercase text-text-muted">
                      {scope.additionsCount} {c.additions}
                    </div>

                    {(status === 'invalid' || status === 'error') && (
                      <div className="mt-5 flex items-start gap-3 text-sm text-red-500">
                        <AlertCircle size={16} className="mt-0.5 flex-shrink-0" />
                        <span>{status === 'invalid' ? c.required : c.errorText}</span>
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={status === 'sending'}
                      className="group w-full mt-7 flex items-center justify-center gap-2 px-8 py-4 rounded-[4px] font-black uppercase tracking-widest text-[12px] text-white disabled:opacity-50 transition-transform hover:scale-[1.01]"
                      style={{ backgroundColor: color }}
                    >
                      {status === 'sending' ? c.sending : c.send}
                      <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                    </button>

                    <p className="mt-4 text-[11px] text-text-muted font-light leading-relaxed text-center">
                      {c.disclaimer}
                    </p>
                  </form>
                </>
              )}
            </div>
          </Motion.div>
        </Motion.div>
      )}
    </AnimatePresence>
  );
};

export default PrototypeScopeModal;
